// src/pages/workflow/DashboardInscripcion.jsx - CÓDIGO COMPLETO
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Paper,
  Container,
  Tooltip,
  IconButton,
  LinearProgress,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  PlayArrow as PlayArrowIcon,
  PersonAdd as PersonAddIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PendingActions as PendingIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { 
  iniciarInscripcion, 
  obtenerEstadoInscripcion, 
  obtenerResumenEstados,
  limpiarError,
  limpiarEstadoWorkflow
} from '../../redux/slices/workflowSlice';
import InformeDocumentos from '../../components/workflow/InformeDocumentos';
import useStyles from '../../theme/useStyles';
import { obtenerEntidadPorId } from '../../actions/EntidadAction';

const DashboardInscripcion = () => {
  const { entidadId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    estadoWorkflow,
    resumenEstados,
    documentosRequeridos,
    loading,
    error
  } = useSelector(state => state.workflow);

  const session = useSelector(state => state.session);
  const { isLoggedIn, username } = session;

  const user = {
    nombre: username || 'Usuario',
    email: username || '',
    rol: session.role || session.rol || 'Usuario'
  };

  const [workflowExiste, setWorkflowExiste] = useState(false);
  const [inicializando, setInicializando] = useState(true);
  const [entidadInfo, setEntidadInfo] = useState(null);
  const [modalInforme, setModalInforme] = useState(false);

  const pasos = [
    { 
      id: 1, 
      nombre: 'Iniciar Inscripción', 
      descripcion: 'Crear proceso de inscripción',
      estado: 'pendiente',
      icon: PlayArrowIcon
    },
    { 
      id: 2, 
      nombre: 'Registro Completo', 
      descripcion: 'Validar entidad, usuarios y contactos',
      estado: 'pendiente',
      icon: PersonAddIcon
    },
    { 
      id: 3, 
      nombre: 'Cargar Documentos', 
      descripcion: 'Subir y radicar documentos requeridos',
      estado: 'pendiente',
      icon: UploadIcon
    },
    { 
      id: 4, 
      nombre: 'Revisión Admin', 
      descripcion: 'Revisión y aprobación por administrador',
      estado: 'pendiente',
      icon: CheckCircleIcon
    },
    { 
      id: 5, 
      nombre: 'Finalización', 
      descripcion: 'Proceso completado',
      estado: 'pendiente',
      icon: CheckCircleIcon
    }
  ];

  // STATE PARA DETECTAR CAMBIOS DE ENTIDAD
  const [entidadIdPrevio, setEntidadIdPrevio] = useState(null);

  useEffect(() => {
    const inicializar = async () => {
      // LIMPIAR ESTADO SI CAMBIÓ LA ENTIDAD
      if (entidadIdPrevio && entidadIdPrevio !== entidadId) {
        console.log('🧹 Cambiando de entidad:', entidadIdPrevio, '→', entidadId);
        dispatch(limpiarEstadoWorkflow());
        setWorkflowExiste(false);
        setEntidadInfo(null);
      }
      
      // ACTUALIZAR ENTIDAD PREVIA
      setEntidadIdPrevio(entidadId);

      if (!entidadId) {
        navigate('/entidades');
        return;
      }

      // RESETEAR ESTADOS LOCALES
      setInicializando(true);
      setWorkflowExiste(false);

      try {
        console.log('Obteniendo estado para entidad:', entidadId);
        
        await cargarInformacionEntidad();
        
        const resultadoEstado = await dispatch(obtenerEstadoInscripcion(entidadId));
        
        console.log('Resultado estado:', resultadoEstado);
        
        // ✅ NUEVA LÓGICA: Verificar si hay workflow basándose en workflowNegocioId
        if (resultadoEstado.payload?.esExitoso && resultadoEstado.payload?.estado) {
          const estado = resultadoEstado.payload.estado;
          
          // ✅ CLAVE: Si workflowNegocioId es 0, no hay workflow iniciado
          if (estado.workflowNegocioId && estado.workflowNegocioId > 0) {
            console.log('✅ Workflow existe, estado:', estado);
            setWorkflowExiste(true);
            
            console.log('Obteniendo resumen para workflowNegocioId:', estado.workflowNegocioId);
            await dispatch(obtenerResumenEstados(estado.workflowNegocioId));
          } else {
            console.log('📝 Entidad existe pero sin workflow iniciado - estado:', estado.nombreEstado);
            setWorkflowExiste(false);
            dispatch(limpiarError());
          }
        } else {
          console.log('❌ Error en respuesta del estado:', resultadoEstado);
          setWorkflowExiste(false);
        } 
      } catch (error) {
        console.error('Error inicializando dashboard:', error);
        setWorkflowExiste(false);
      } finally {
        setInicializando(false);
      }
    };

    inicializar();
  }, [entidadId, entidadIdPrevio, dispatch, navigate]);

  // ✅ NUEVA FUNCIÓN: Verificar si es estado "Sin workflow iniciado"
  const esSinWorkflowIniciado = () => {
    return estadoWorkflow && 
          estadoWorkflow.workflowNegocioId === 0 && 
          estadoWorkflow.nombreEstado === "Sin iniciar workflow";
  };

  useEffect(() => {
    if (resumenEstados && entidadInfo && resumenEstados.estadoWorkflow) {
      console.log('🎯 Estado del workflow actualizado:', resumenEstados.estadoWorkflow);
      console.log('📊 Resumen completo:', resumenEstados);
    }
  }, [resumenEstados, entidadInfo]);

  // CLEANUP AL DESMONTAR COMPONENTE
  useEffect(() => {
    // Cleanup cuando el componente se desmonta
    return () => {
      console.log('🧹 Limpiando estado al desmontar DashboardInscripcion');
      dispatch(limpiarEstadoWorkflow());
    };
  }, [dispatch]);

  const cargarInformacionEntidad = async () => {
    try {
      console.log('Cargando información real de la entidad:', entidadId);
      
      const entidadResponse = await obtenerEntidadPorId(entidadId);
      
      if (entidadResponse.status === 200) {
        console.log('Información de entidad cargada:', entidadResponse.data);
        setEntidadInfo({
          id: entidadResponse.data.id,
          nombre: entidadResponse.data.nombre || entidadResponse.data.razonSocial || `Entidad ${entidadId}`,
          tipoEntidad: entidadResponse.data.tipoEntidad,
          nit: entidadResponse.data.nit,
          estado: entidadResponse.data.estado || 'Activa'
        });
      } else {
        console.error('Error en respuesta de entidad:', entidadResponse);
        setEntidadInfo({ 
          id: entidadId, 
          nombre: `Entidad ID: ${entidadId}` 
        });
      }
    } catch (error) {
      console.error('Error cargando información de entidad:', error);
      setEntidadInfo({ 
        id: entidadId, 
        nombre: `Entidad ID: ${entidadId}` 
      });
    }
  };

  const handleIniciarInscripcion = async () => {
    try {
      console.log('Iniciando inscripción para entidad:', entidadId);
      
      const resultado = await dispatch(iniciarInscripcion(entidadId));
      console.log('Resultado iniciar inscripción:', resultado);
      
      if (resultado.payload?.esExitoso) {
        console.log('Inscripción iniciada exitosamente');
        setWorkflowExiste(true);
        
        await actualizarEstadoCompleto();
        
      } else {
        console.error('Error al iniciar inscripción:', resultado.payload);
      }
    } catch (error) {
      console.error('Error iniciando inscripción:', error);
    }
  };

  const actualizarEstadoCompleto = async () => {
    try {
      console.log('🔄 Actualizando estado completo después de iniciar inscripción...');
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const resultadoEstado = await dispatch(obtenerEstadoInscripcion(entidadId));
      console.log('🔍 Estado actualizado:', resultadoEstado);
      
      if (resultadoEstado.payload?.esExitoso && resultadoEstado.payload?.estado?.workflowNegocioId) {
        const workflowNegocioId = resultadoEstado.payload.estado.workflowNegocioId;
        console.log('🔹 Obteniendo resumen para WorkflowNegocioId:', workflowNegocioId);
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const resultadoResumen = await dispatch(obtenerResumenEstados(workflowNegocioId));
        console.log('📊 Resumen actualizado:', resultadoResumen);
      }
      
      console.log('✅ Estado completo actualizado exitosamente');
      
    } catch (error) {
      console.error('❌ Error actualizando estado completo:', error);
    }
  };

  const navegarAPaso = (numeroPaso) => {
    const rutas = {
      2: `/inscripcion/${entidadId}/registro`,
      3: `/inscripcion/${entidadId}/documentos`,
      4: `/inscripcion/${entidadId}/revision`,
      5: `/inscripcion/${entidadId}/finalizacion`
    };

    if (rutas[numeroPaso]) {
      navigate(rutas[numeroPaso]);
    }
  };

  // *** FUNCIONES AUXILIARES CORREGIDAS SEGÚN ESTADOS REALES ***
  const determinarEstadoPasos = () => {
    if (!workflowExiste || !resumenEstados) {
      return pasos.map(paso => ({
        ...paso,
        estado: paso.id === 1 ? 'completado' : 'pendiente'
      }));
    }

    return pasos.map(paso => {
      switch (paso.id) {
        case 1: // INICIO
          return { ...paso, estado: 'completado' };
        
        case 2: // REGISTRO COMPLETO
          // ✅ CORRECCIÓN CRÍTICA: Si el workflow está completado (estado 5), el registro DEBE estar completo
          if (resumenEstados?.estadoWorkflow === 5) {
            return { ...paso, estado: 'completado' };
          }
          if (resumenEstados?.registroCompleto) {
            return { ...paso, estado: 'completado' };
          } else if (resumenEstados?.estadoWorkflow >= 2) {
            return { ...paso, estado: 'en_progreso' };
          }
          return { ...paso, estado: 'pendiente' };
        
        case 3: // CARGAR DOCUMENTOS
          if (resumenEstados?.totalDocumentos === 0) {
            return { ...paso, estado: 'no_configurado' };
          }
          
          // ✅ CORRECCIÓN: Si el workflow está completado (estado 5), mostrar como completado
          if (resumenEstados?.estadoWorkflow === 5) {
            return { ...paso, estado: 'completado' };
          }

          // Si todos están aprobados (estado 4), completado
          if (resumenEstados?.documentosAprobados === resumenEstados?.totalDocumentos && resumenEstados?.totalDocumentos > 0) {
            return { ...paso, estado: 'completado' };
          }
          
          // Si todos los documentos están radicados (estado 2) o mejor, el paso está completado
          const documentosRadicadosOMejor = (resumenEstados.documentosRadicados || 0) + 
                                           (resumenEstados.documentosEnRevision || 0) + 
                                           (resumenEstados.documentosAprobados || 0);
          
          if (documentosRadicadosOMejor === resumenEstados?.totalDocumentos) {
            return { ...paso, estado: 'completado' };
          }
          
          // Si hay algunos documentos subidos o algunos radicados (pero no todos), en progreso
          if (resumenEstados?.documentosSubidos > 0 || documentosRadicadosOMejor > 0) {
            return { ...paso, estado: 'en_progreso' };
          }
          
          return { ...paso, estado: 'pendiente' };
        
        case 4: // REVISIÓN ADMIN
          // ✅ CORRECCIÓN: Si el workflow está completado (estado 5), mostrar como completado
          if (resumenEstados?.estadoWorkflow === 5) {
            return { ...paso, estado: 'completado' };
          } else if (resumenEstados?.estadoWorkflow === 6) {
            return { ...paso, estado: 'error' };
          } else if (resumenEstados?.documentosEnRevision > 0) {
            return { ...paso, estado: 'en_progreso' };
          }
          // Si hay documentos radicados esperando revisión
          else if (resumenEstados?.documentosRadicados > 0) {
            return { ...paso, estado: 'en_progreso' };
          }
          return { ...paso, estado: 'pendiente' };
        
        case 5: // FINALIZACIÓN
          // ✅ CORRECCIÓN: Si el workflow está completado (estado 5), mostrar como completado
          if (resumenEstados?.estadoWorkflow === 5) {
            return { ...paso, estado: 'completado' };
          } else if (resumenEstados?.estadoWorkflow === 6) {
            return { ...paso, estado: 'error' };
          }
          return { ...paso, estado: 'pendiente' };
        
        default:
          return paso;
      }
    });
  };

  const calcularPorcentajeReal = () => {
    // Si no hay workflow iniciado, 0%
    if (esSinWorkflowIniciado() || !workflowExiste) {
      return 0;
    }
    
    if (!resumenEstados) {
      return workflowExiste ? 25 : 0;
    }
    
    // ✅ CORRECCIÓN CRÍTICA: Si el workflow está completado (estado 5), debe ser 100%
    if (resumenEstados.estadoWorkflow === 5) {
      return 100;
    }
    
    let progreso = 0;
    
    // Paso 1: Workflow iniciado (25%)
    if (workflowExiste) progreso += 25;
    
    // Paso 2: Registro completo (25%)
    if (resumenEstados.registroCompleto) progreso += 25;
    
    // Paso 3: Documentos (25%) - basado en documentos aprobados
    if (resumenEstados.totalDocumentos > 0) {
      const porcentajeDocumentos = (resumenEstados.documentosAprobados / resumenEstados.totalDocumentos) * 25;
      progreso += porcentajeDocumentos;
    }
    
    // Paso 4: Revisión final (25%) - solo si todos los documentos están aprobados
    if (resumenEstados.documentosAprobados === resumenEstados.totalDocumentos && resumenEstados.totalDocumentos > 0) {
      progreso += 25;
    }
    
    return Math.round(progreso);
  };

  const obtenerTextoDocumentos = () => {
    if (!resumenEstados || resumenEstados.totalDocumentos === 0) {
      return 'Sin configurar';
    }
    
    const aprobados = resumenEstados.documentosAprobados || 0;
    const total = resumenEstados.totalDocumentos || 0;
    const subidos = resumenEstados.documentosSubidos || 0;
    const radicados = resumenEstados.documentosRadicados || 0;
    
    if (aprobados === total) {
      return `${aprobados} aprobados de ${total}`;
    } else if (subidos > 0) {
      return `${subidos} subidos, ${radicados} radicados de ${total}`;
    } else {
      return `${aprobados} aprobados de ${total}`;
    }
  };

  const obtenerTextoRevision = () => {
    if (!resumenEstados || resumenEstados.totalDocumentos === 0) {
      return 'Pendiente';
    }
    
    const aprobados = resumenEstados.documentosAprobados || 0;
    const enRevision = resumenEstados.documentosEnRevision || 0;
    const radicados = resumenEstados.documentosRadicados || 0;
    const total = resumenEstados.totalDocumentos || 0;
    
    if (aprobados === total && total > 0) {
      return 'Proceso completado';
    }
    
    if (enRevision > 0) {
      return `${enRevision} en revisión`;
    }
    
    if (radicados > 0) {
      return `${radicados} pendientes de revisión`;
    }
    
    if (aprobados > 0) {
      return `${aprobados} aprobados`;
    }
    
    return 'Pendiente inicio';
  };

  const obtenerMensajeEstadoActual = () => {

    // Caso especial: Sin workflow iniciado pero entidad válida
    if (esSinWorkflowIniciado()) {
      return 'La entidad está registrada pero el proceso de inscripción no ha sido iniciado. Haga clic en "Iniciar Inscripción" para comenzar.';
    }

    if (!workflowExiste) {
      return 'El proceso de inscripción no ha sido iniciado. Haga clic en "Iniciar Inscripción" para comenzar.';
    }

    if (!resumenEstados) {
      return 'Cargando estado del proceso...';
    }

    // ✅ CASO ESPECIAL: Workflow completado exitosamente (DEBE IR PRIMERO)
    if (resumenEstados?.estadoWorkflow === 5) {
      return '🎉 ¡Felicitaciones! Su inscripción ha sido aprobada y completada exitosamente';
    }
    
    if (resumenEstados && !resumenEstados.registroCompleto) {
      return 'Pendiente: Complete el registro de entidad, usuarios y contactos';
    }

    const pendientesSubir = (resumenEstados.documentosPendientes || 0) + (resumenEstados.documentosPendientesRecarga || 0);
    const pendientesRadicar = resumenEstados.documentosSubidos || 0;
    
    if (pendientesSubir > 0) {
      return `Pendiente: ${pendientesSubir} documentos por subir`;
    }
    
    if (pendientesRadicar > 0) {
      return `Pendiente: ${pendientesRadicar} documentos subidos requieren radicación`;
    }

    if (resumenEstados.documentosEnRevision > 0) {
      return `En revisión: ${resumenEstados.documentosEnRevision} documentos siendo revisados`;
    }
    if (resumenEstados.estadoWorkflow === 6) {
      return 'Su inscripción ha sido rechazada. Consulte los comentarios del administrador.';
    }

    return 'Estado del proceso actualizado';
  
  };

  const obtenerTipoAlerta = () => {
    if (esSinWorkflowIniciado()) {
      return 'info';
    }
    if (!workflowExiste) {
      return 'info';
    }
    if (!resumenEstados) return 'info';
    if (resumenEstados?.estadoWorkflow === 5) return 'success';
    if (resumenEstados?.estadoWorkflow === 6) return 'error';
    if (resumenEstados?.requiereIntervencion) return 'warning';
    return 'info';
  };

  const getStepIcon = (estado) => {
    switch (estado) {
      case 'completado':
        return <CheckCircleIcon color="success" />;
      case 'en_progreso':
        return <CircularProgress size={20} />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'no_configurado':
        return <WarningIcon color="disabled" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getStatusChip = (estado, label) => {
    const chipProps = {
      completado: { color: 'success', label: label || 'Completo' },
      en_progreso: { color: 'warning', label: label || 'En Progreso' },
      error: { color: 'error', label: label || 'Error' },
      pendiente: { color: 'default', label: label || 'Pendiente' },
      no_configurado: { color: 'default', label: label || 'Sin configurar' },
      subido_pendiente_radicacion: { color: 'info', label: label || 'Pendiente Radicación' }
    };

    return <Chip {...chipProps[estado]} size="small" />;
  };

  // ✅ FUNCIONES para manejar el informe
  const handleAbrirInforme = () => {
    setModalInforme(true);
  };

  const handleCerrarInforme = () => {
    setModalInforme(false);
  };

  // ✅ VERIFICAR si el informe debe estar disponible
  const puedeGenerarInforme = () => {
    return resumenEstados && 
           resumenEstados?.totalDocumentos > 0 && 
           resumenEstados?.estadoWorkflow >= 3;
  };

  // ✅ BOTÓN ESPECIAL para workflow completado
  const mostrarBotonInformeCompleto = () => {
    return resumenEstados?.estadoWorkflow === 5;
  };

  if (inicializando) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column'
      }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Cargando dashboard de inscripción...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minHeight: '100vh',
      p: 2,
      boxSizing: 'border-box'
    }}>
      <Card className={classes.card}
        sx={{ 
          width: '100%',
          maxWidth: 1400,
          margin: 'auto',
          mt: 4,
          p: 2
        }}
      >
        <CardHeader 
          classes={{ title: classes.cardTitle }}
          avatar={
            <DashboardIcon style={{ fontSize: "large", color: "#494949" }} />
          }
          title="Dashboard de Inscripción"
          // title={`Dashboard de Inscripción - ${entidadInfo?.nombre || 'Cargando...'}`}
          subheader={`${entidadInfo?.nombre || `Entidad ID: ${entidadId}`} | Usuario: ${user?.nombre || user?.email}`}
          //subheader={obtenerMensajeEstadoActual()}
          action={
            <Grid container spacing={1} alignItems="center" justifyContent="center">
              {/* ✅ BOTÓN DE INFORME ESPECIAL para workflow completado */}
              {mostrarBotonInformeCompleto() && (
                <Grid item>
                  <Tooltip title="Generar Informe Final - Proceso Completado" arrow>
                    <Button
                      onClick={handleAbrirInforme}
                      variant="contained"
                      color="success"
                      startIcon={<ReportIcon />}
                      size="small"
                      sx={{ 
                        fontWeight: 'bold',
                        boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 12px rgba(76, 175, 80, 0.4)'
                        }
                      }}
                    >
                      Informe Final
                    </Button>
                  </Tooltip>
                </Grid>
              )}

              <Grid item>
                <Tooltip title="Actualizar Estado" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={actualizarEstadoCompleto}
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.button}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              
              <Grid item>
                <Tooltip title="Inicio" arrow classes={{ tooltip: classes.tooltip }}>
                  <IconButton
                    onClick={() => navigate('/')}
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={classes.button}
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
              
              {(!workflowExiste || esSinWorkflowIniciado()) && (
                <Grid item>
                  <Button
                    onClick={handleIniciarInscripcion}
                    disabled={loading.iniciarInscripcion}
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    size="small"
                  >
                    {loading.iniciarInscripcion ? 'Iniciando...' : 'Iniciar Inscripción'}
                  </Button>
                </Grid>
              )}
            </Grid>
          }
          sx={{ 
            '& .MuiCardHeader-title': { fontSize: '1.25rem' },
            pr: 6
          }}
        />
        
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          {/* Alerta del estado actual */}
          <Box sx={{ mb: 3 }}>
            <Alert severity={obtenerTipoAlerta()}>
              <AlertTitle>Estado del Proceso</AlertTitle>
              {obtenerMensajeEstadoActual()}
            </Alert>
          </Box>

          {/* Barra de progreso - SOLO mostrar si no hay workflow iniciado */}
          {(!workflowExiste || esSinWorkflowIniciado()) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Progreso del Proceso
              </Typography>
              
              <Paper elevation={1} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    Completado
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    0%
                  </Typography>
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={0} 
                  sx={{ height: 12, borderRadius: 6, mb: 2 }}
                  color="primary"
                />
                
                <Typography variant="body2" color="text.secondary" align="center">
                  Proceso sin iniciar - 5 pasos por completar
                </Typography>
              </Paper>
            </Box>
          )}
          {/* Barra de progreso */}
          {!workflowExiste && resumenEstados && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Progreso del Proceso
              </Typography>
              
              <Paper elevation={1} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                    Completado
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {calcularPorcentajeReal()}%
                  </Typography>
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={calcularPorcentajeReal()} 
                  sx={{ height: 12, borderRadius: 6, mb: 2 }}
                  color={calcularPorcentajeReal() === 100 ? 'success' : 'primary'}
                />
                
                <Typography variant="body2" color="text.secondary" align="center">
                  {calcularPorcentajeReal() === 100 
                    ? 'Proceso completado exitosamente' 
                    : `${5 - Math.floor(calcularPorcentajeReal() / 20)} pasos restantes`
                  }
                </Typography>
              </Paper>
            </Box>
          )}
          {/* Contenido principal */}
          {workflowExiste && !esSinWorkflowIniciado() && resumenEstados && (
            <>
              <Grid container spacing={3}  sx={{ mb: 3 }}>
                {/* Columna izquierda: Stepper */}
                <Grid item xs={12} lg={5}>
                  <Paper elevation={1} sx={{ p: 3, height: 'fit-content' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Pasos del Proceso
                    </Typography>
                    
                    <Stepper orientation="vertical">
                      {determinarEstadoPasos().map((paso) => (
                        <Step key={paso.id} active={true} completed={paso.estado === 'completado'}>
                          <StepLabel
                            StepIconComponent={() => getStepIcon(paso.estado)}
                            onClick={() => paso.estado !== 'pendiente' && navegarAPaso(paso.id)}
                            sx={{ cursor: paso.estado !== 'pendiente' ? 'pointer' : 'default' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {paso.nombre}
                              </Typography>
                              {getStatusChip(paso.estado)}
                            </Box>
                          </StepLabel>
                          <StepContent>
                            <Typography variant="body2" color="text.secondary">
                              {paso.descripcion}
                            </Typography>
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  </Paper>
                </Grid>

                {/* Columna derecha: Resumen de Estados y Acciones Disponibles */}
                <Grid item xs={12} lg={7}>
                  {/* Resumen de Estados */}
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Resumen de Estados
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {/* Card Registro */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { elevation: 4 } }}
                          onClick={() => navegarAPaso(2)}
                        >
                          <AssignmentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Registro
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Entidad, usuarios y contactos
                          </Typography>
                          {getStatusChip(resumenEstados.registroCompleto ? 'completado' : 'pendiente', 
                            resumenEstados.registroCompleto ? 'Completo' : 'Pendiente')}
                        </Paper>
                      </Grid>

                      {/* Card Documentos */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { elevation: 4 } }}
                          onClick={() => navegarAPaso(3)}
                        >
                          <UploadIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Documentos
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {obtenerTextoDocumentos()}
                          </Typography>
                          {getStatusChip(
                            resumenEstados.documentosAprobados === resumenEstados.totalDocumentos && resumenEstados.totalDocumentos > 0 
                              ? 'completado' 
                              : resumenEstados.documentosEnRevision > 0 || resumenEstados.documentosRadicados > 0 
                                ? 'en_progreso' 
                                : 'pendiente'
                          )}
                        </Paper>
                      </Grid>

                      {/* Card Revisión */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper 
                          elevation={2} 
                          sx={{ p: 2, textAlign: 'center', cursor: 'pointer', '&:hover': { elevation: 4 } }}
                          onClick={() => navegarAPaso(4)}
                        >
                          <CheckCircleIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Revisión
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {obtenerTextoRevision()}
                          </Typography>
                          {getStatusChip(
                            resumenEstados.documentosAprobados === resumenEstados.totalDocumentos && resumenEstados.totalDocumentos > 0
                              ? 'completado' 
                              : resumenEstados.documentosEnRevision > 0 
                                ? 'en_progreso' 
                                : 'pendiente'
                          )}
                        </Paper>
                      </Grid>
                      
                      {/* Card de Historial */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper 
                          elevation={1} 
                          sx={{ 
                            p: 2, 
                            textAlign: 'center', 
                            cursor: 'pointer',
                            border: '2px dashed',
                            borderColor: 'grey.400',
                            '&:hover': { 
                              borderColor: 'grey.600',
                              bgcolor: 'grey.50' 
                            }
                          }}
                          onClick={() => navigate(`/inscripcion/${entidadId}/historial`)}
                        >
                          <HistoryIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                            Ver Historial
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Seguimiento completo del proceso
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>

                      {/* ✅ NUEVA CARD: Generar Informe */}
                      {puedeGenerarInforme() && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Paper 
                            elevation={2} 
                            sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              cursor: 'pointer',
                              border: mostrarBotonInformeCompleto() ? '2px solid' : '2px dashed',
                              borderColor: mostrarBotonInformeCompleto() ? 'success.main' : 'primary.main',
                              bgcolor: mostrarBotonInformeCompleto() ? 'success.light' : 'background.paper',
                              '&:hover': { 
                                borderColor: mostrarBotonInformeCompleto() ? 'success.dark' : 'primary.dark',
                                bgcolor: mostrarBotonInformeCompleto() ? 'success.main' : 'primary.light',
                                color: 'white',
                                '& .MuiTypography-root': { color: 'white' }
                              }
                            }}
                            onClick={handleAbrirInforme}
                          >
                            <ReportIcon 
                              color={mostrarBotonInformeCompleto() ? "success" : "primary"} 
                              sx={{ fontSize: 40, mb: 1 }} 
                            />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {mostrarBotonInformeCompleto() ? 'Informe Final' : 'Generar Informe'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {mostrarBotonInformeCompleto() 
                                ? 'Proceso completado - Descargar informe' 
                                : 'Reporte de documentos'
                              }
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                {/* Acciones rápidas adicionales */}
                {resumenEstados.totalDocumentos > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      Acciones Rápidas
                    </Typography>
                    
                    <Paper elevation={1} sx={{ p: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Button
                            onClick={() => navigate(`/inscripcion/${entidadId}/documentos`)}
                            variant="outlined"
                            fullWidth
                            startIcon={<UploadIcon />}
                          >
                            Gestionar Documentos
                          </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Button
                            onClick={() => navigate(`/inscripcion/${entidadId}/revision`)}
                            variant="outlined"
                            fullWidth
                            startIcon={<CheckCircleIcon />}
                            disabled={resumenEstados.documentosRadicados === 0}
                          >
                            Ir a Revisión
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                )}
              </Grid>
            </>
          )}

          {/* Mostrar mensaje cuando no hay workflow */}
          {(!workflowExiste || esSinWorkflowIniciado()) && (
            <Box sx={{ mb: 3 }}>
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  border: '2px dashed',
                  borderColor: 'primary.main'
                }}
              >
                <PlayArrowIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                  Proceso no iniciado
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Para comenzar el proceso de inscripción, haga clic en el botón "Iniciar Inscripción" en la parte superior derecha.
                </Typography>
                <Button
                  onClick={handleIniciarInscripcion}
                  disabled={loading.iniciarInscripcion}
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                >
                  {loading.iniciarInscripcion ? 'Iniciando...' : 'Iniciar Inscripción'}
                </Button>
              </Paper>
            </Box>
          )}

          {/* Información de estado para debugging */}
          {(workflowExiste || esSinWorkflowIniciado()) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Estado del Workflow: {resumenEstados?.estadoWorkflow} | 
                Documentos: {resumenEstados?.documentosAprobados}/{resumenEstados?.totalDocumentos} aprobados |
                Última actualización: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          )}
        </CardContent>

        {/* ✅ MODAL DEL INFORME */}
        <InformeDocumentos
          open={modalInforme}
          onClose={handleCerrarInforme}
          entidadInfo={entidadInfo}
          documentos={documentosRequeridos}
          resumenEstados={resumenEstados}
          estadoWorkflow={resumenEstados?.estadoWorkflow}
        />
      </Card>
    </Box>
  );
};

export default DashboardInscripcion;